import * as z from 'zod'

import type { AdminAffiliateTierSchema, AffiliateAdminCreatePartnerSchema } from '@/api/gen'
import { toRate } from '@/utils/to-rate'

/**
 * The domain SSOT for the admin "New partner" form. It mirrors the backend's
 * `AffiliateAdminCreatePartnerSchema` (gto-wizard, `POST /admin/partners/`):
 * display name 3–128 characters, a well-formed email, 1–10 coupon codes of at
 * most 64 characters with no code listed twice, a tier, and an optional
 * two-letter country. The server stays the authority for what the client
 * cannot know — a taken name, email or code, or an email with no GTO Wizard
 * account — and names the failing field in its 422.
 */

export const MIN_DISPLAY_NAME_LENGTH = 3
export const MAX_DISPLAY_NAME_LENGTH = 128
export const MAX_COUPON_CODES = 10
export const MAX_COUPON_CODE_LENGTH = 64

const V = {
  displayName: 'newPartner.validation.displayName',
  email: 'newPartner.validation.email',
  codesRequired: 'newPartner.validation.codesRequired',
  codesTooMany: 'newPartner.validation.codesTooMany',
  codeTooLong: 'newPartner.validation.codeTooLong',
  codesDuplicate: 'newPartner.validation.codesDuplicate',
  tierRequired: 'newPartner.validation.tierRequired',
  country: 'newPartner.validation.country',
} as const

/**
 * Split the coupon-codes field into codes. The admin types them separated by
 * commas, spaces or new lines; the backend stores every code uppercase, so the
 * split uppercases too and a duplicate check here matches the server's.
 */
export const parseCouponCodes = (value: string): string[] =>
  value
    .split(/[\s,]+/)
    .map((code) => code.trim().toUpperCase())
    .filter((code) => code !== '')

export const newPartnerFormSchema = z.object({
  displayName: z
    .string()
    .trim()
    .min(MIN_DISPLAY_NAME_LENGTH, V.displayName)
    .max(MAX_DISPLAY_NAME_LENGTH, V.displayName),
  email: z.string().trim().pipe(z.email(V.email)),
  couponCodes: z.string().superRefine((value, ctx) => {
    const codes = parseCouponCodes(value)
    if (codes.length === 0) {
      ctx.addIssue({ code: 'custom', message: V.codesRequired })

      return
    }
    if (codes.length > MAX_COUPON_CODES) {
      ctx.addIssue({ code: 'custom', message: V.codesTooMany })
    }
    if (codes.some((code) => code.length > MAX_COUPON_CODE_LENGTH)) {
      ctx.addIssue({ code: 'custom', message: V.codeTooLong })
    }
    if (new Set(codes).size !== codes.length) {
      ctx.addIssue({ code: 'custom', message: V.codesDuplicate })
    }
  }),
  tierId: z.string().min(1, V.tierRequired),
  country: z
    .string()
    .trim()
    .refine((value) => value === '' || /^[A-Za-z]{2}$/.test(value), V.country),
})

export type NewPartnerFormValues = z.infer<typeof newPartnerFormSchema>

export const NEW_PARTNER_FORM_DEFAULTS: NewPartnerFormValues = {
  displayName: '',
  email: '',
  couponCodes: '',
  tierId: '',
  country: '',
}

/**
 * The tier a new partner starts on: the lowest commission rate on offer. The
 * backend picks its entry tier the same way (`entry_tier()` in
 * `services/partner.py`), so the form pre-selects what an activation with no
 * explicit tier would give. An admin changes it only for a bespoke agreement.
 */
export const defaultTierId = (tiers: readonly AdminAffiliateTierSchema[]): string => {
  const entry = tiers.reduce<AdminAffiliateTierSchema | undefined>(
    (lowest, tier) =>
      lowest === undefined ||
      toRate(tier.commission_rate, 'new-partner tier rate:') <
        toRate(lowest.commission_rate, 'new-partner tier rate:')
        ? tier
        : lowest,
    undefined,
  )

  return entry === undefined ? '' : String(entry.id)
}

/**
 * Map the form's field names onto the wire field names the backend's 422
 * reports, so a server error lands on the field the admin must correct.
 */
export const NEW_PARTNER_SERVER_FIELDS = {
  display_name: 'displayName',
  email: 'email',
  coupon_codes: 'couponCodes',
  tier_id: 'tierId',
  country: 'country',
} as const satisfies Partial<
  Record<keyof AffiliateAdminCreatePartnerSchema, keyof NewPartnerFormValues>
>

export const buildNewPartnerRequest = (
  values: NewPartnerFormValues,
): AffiliateAdminCreatePartnerSchema => ({
  display_name: values.displayName.trim(),
  email: values.email.trim(),
  coupon_codes: parseCouponCodes(values.couponCodes),
  tier_id: Number(values.tierId),
  country: values.country.trim().toUpperCase(),
})
